import { ref, computed } from 'vue';
import { t } from '@/utils/i18n-helper';
import { config } from '@/config';
import cambioButacaService from '@/services/perfil/cambioButaca';
import { toError } from '@/services/perfil/_normalize';
import { getPagoEnCursoSessionId, limpiarPagoEnCurso } from '@/utils/pagoEnCurso';

/**
 * useCambioButacaConfirmacion — Estado y operaciones del paso de confirmación
 * para el flujo de cambio de asiento (butaca o plaza).
 *
 * Responsabilidades:
 *  - Reserva del asiento seleccionado (bloqueo en backend + obtención de sessionId).
 *  - Sesión: sessionId, sessionEndsAt (para el CountdownBar).
 *  - Datos para el siguiente paso de pago: precio_total, jet_id, formulario
 *    (HTML del TPV Redsys), price_management_interno.
 *  - Confirmación gratuita (precio_total = 0): endpoint confirmar.
 *  - Consulta post-pago: endpoint info.
 *  - Liberación de sesión (borrarSesion) para beforeunload y timer expirado.
 *  - Guards: puedeConfirmar, requierePago.
 *
 * El backend de `reservar()` puede devolver EITHER `jet_id` (pasarela Paycomet,
 * flujo actual con PagoPaycometComponent) OR `formulario` (HTML con un form de
 * TPV Redsys que debe auto-submitearse). El procesamiento se delega a un paso
 * posterior del wizard (`PASO_PAGO`); este composable sólo expone los datos
 * necesarios para que dicho paso construya su UI/formulario.
 */
export function useCambioButacaConfirmacion() {

    // ─── State: reserva ───────────────────────────────────────────────────
    const loadingReservar = ref(false);
    const errorReservar = ref(null);

    /** ID de sesión retornado por el endpoint reservar. */
    const sessionId = ref(null);

    /** Timestamp de expiración de la sesión (ISO 8601 o Unix timestamp). */
    const sessionEndsAt = ref(null);

    // ─── State: pago ──────────────────────────────────────────────────────
    const precioTotal = ref(0);
    const jetId = ref(null);

    /**
     * HTML del formulario de TPV Redsys devuelto por `reservar()`.
     * `null` cuando el pago va por Paycomet (`jetId`) o es gratuito.
     * Cuando está presente, el paso de pago debe renderizarlo oculto
     * (`#formulario-compra`) y auto-submitearlo tras el click en "Confirmar".
     * @type {import('vue').Ref<string|null>}
     */
    const formulario = ref(null);

    const priceManagementInterno = ref(null);
    const importe = ref(0);
    const importeCambio = ref(0);
    const priceManagement = ref(0);

    // ─── State: confirmación gratuita ─────────────────────────────────────
    const loadingConfirmar = ref(false);
    const errorConfirmar = ref(null);

    /** Resultado del confirmar gratuito: { n_pedido, session_id }. */
    const resultadoCambio = ref(null);

    // ─── State: info post-pago ────────────────────────────────────────────
    const resultadoInfo = ref(null);
    const loadingInfo = ref(false);

    // ─── Computed ─────────────────────────────────────────────────────────

    /**
     * `true` cuando el cambio requiere pasarela de pago.
     */
    const requierePago = computed(() => precioTotal.value > 0);

    /**
     * Puede confirmar siempre (los términos GDPR se gestionan únicamente
     * en el flujo de pago, no en cambio-asiento).
     */
    const puedeConfirmar = computed(() => true);

    /**
     * `true` cuando hay gastos de gestión visibles que mostrar al usuario.
     */
    const mostrarGastosGestion = computed(() => priceManagement.value > 0);

    // ─── API: reservar ────────────────────────────────────────────────────

    /**
     * Reserva la butaca seleccionada en el backend, bloqueándola y creando
     * la sesión de cambio.
     *
     * @param {string|number} abonadoId
     * @param {{ zona_id: string|number, asiento: Object }} seatData
     * @returns {Promise<Object>} Resultado del backend con session_id, session_ends_at, etc.
     * @throws {Error} Si la reserva falla (butaca ocupada, error de red, etc.)
     */
    const reservar = async(abonadoId, seatData) => {
        if (!abonadoId) throw new Error('Missing abonadoId');

        loadingReservar.value = true;
        errorReservar.value = null;

        // Una reserva nueva invalida cualquier pago APM anterior: el flag
        // de pago en curso ya no debe proteger esta sesión.
        limpiarPagoEnCurso();

        try {
            // seatData ya viene con los campos correctos según el tipo de butaca:
            //   - Camino 1 (numerada): zona_id, tipo_abono_id, region_id, row, seat
            //   - Camino 2 (plaza):    zona_id, tipo_abono_id, plaza_id
            const payload = { ...seatData };

            const result = await cambioButacaService.reservar(abonadoId, payload);

            sessionId.value = result.session_id ?? null;
            sessionEndsAt.value = result.session_ends_at ?? result.sessionEndsAt ?? null;
            precioTotal.value = parseFloat(result.precio_total ?? result.precioTotal ?? 0) || 0;
            importe.value = parseFloat(result.importe ?? 0) || 0;
            importeCambio.value = parseFloat(result.importe ?? 0) || 0;
            priceManagement.value = parseFloat(result.price_management ?? 0) || 0;
            jetId.value = result.jet_id ?? result.jetId ?? null;
            // Normalizar: un formulario vacío o solo espacios no es un TPV Redsys
            // real. Se deja en null para que la confirmación muestre el error de
            // pago no disponible en lugar de intentar auto-submitear un form que
            // no existe (dead-end sin botón ni recuperación).
            formulario.value = typeof result.formulario === 'string' && result.formulario.trim()
                ? result.formulario.trim()
                : null;
            priceManagementInterno.value = result.price_management_interno
                ?? result.priceManagementInterno
                ?? null;

            return result;
        } catch (err) {
            const normalized = toError(err);
            errorReservar.value = normalized.message ??
                t('perfil.cambio_asiento.stepper.error_reservar');
            if (process.env.NODE_ENV !== 'production') {
                console.error('[useCambioButacaConfirmacion] Error al reservar:', err);
            }
            throw err;
        } finally {
            loadingReservar.value = false;
        }
    };

    // ─── API: confirmar gratuito ──────────────────────────────────────────

    /**
     * Confirma el cambio de butaca sin coste (precio_total = 0).
     *
     * @param {string|number} abonadoId
     * @returns {Promise<boolean>} `true` si el cambio se confirmó correctamente
     */
    const confirmar = async abonadoId => {
        if (!abonadoId || !sessionId.value) return false;

        loadingConfirmar.value = true;
        errorConfirmar.value = null;

        try {
            const payload = {
                session_id: String(sessionId.value),
            };

            const result = await cambioButacaService.confirmar(abonadoId, payload);
            resultadoCambio.value = result;
            return true;
        } catch (err) {
            const normalized = toError(err);
            errorConfirmar.value = normalized.message ??
                t('perfil.cambio_asiento.stepper.error_confirmar');
            if (process.env.NODE_ENV !== 'production') {
                console.error('[useCambioButacaConfirmacion] Error al confirmar:', err);
            }
            return false;
        } finally {
            loadingConfirmar.value = false;
        }
    };

    // ─── API: info post-pago ──────────────────────────────────────────────

    /**
     * Consulta el endpoint info para obtener el resultado del cambio
     * después de un pago exitoso (el swap ya lo ejecutó el webhook).
     *
     * @param {string|number} abonadoId
     * @param {string} [tpvOrder] — Orden TPV opcional
     * @returns {Promise<Object|null>} Datos del cambio o null si falla
     */
    const consultarInfo = async(abonadoId, tpvOrder = null) => {
        if (!abonadoId || !sessionId.value) return null;

        loadingInfo.value = true;
        try {
            const result = await cambioButacaService.getInfo(abonadoId, sessionId.value, tpvOrder);
            resultadoInfo.value = result;
            return result;
        } catch (err) {
            if (process.env.NODE_ENV !== 'production') {
                console.error('[useCambioButacaConfirmacion] Error al consultar info:', err);
            }
            return null;
        } finally {
            loadingInfo.value = false;
        }
    };

    // ─── Liberación de sesión ─────────────────────────────────────────────

    /**
     * Llama al backend para borrar la sesión activa y liberar la butaca.
     * Idempotente: si no hay sessionId, retorna sin hacer nada.
     * Siempre limpia el sessionId local al finalizar.
     *
     * Esta función debe llamarse desde:
     *  - `beforeunload` (vista padre)
     *  - `onTimerExpirado` (cuando el CountdownBar llega a 0)
     *  - `prevStep` cuando el usuario retrocede de Confirmación a Butacas
     *
     * NO libera la sesión cuando hay un pago APM hosted (Bizum) en curso
     * para ese mismo session_id: la navegación a la pasarela desmonta la
     * SPA y dispararía esta liberación antes de que el webhook procese el
     * pago completado. El flag se marca en `useBizumPago` justo antes de
     * redirigir y se limpia al volver de la pasarela o al reservar de nuevo.
     */
    const liberarSesion = async(abonadoIdOverride = null) => {
        if (!sessionId.value) return;
        const currentSessionId = sessionId.value;
        if (getPagoEnCursoSessionId() === currentSessionId) {
            return;
        }
        sessionId.value = null;
        try {
            if (abonadoIdOverride) {
                await cambioButacaService.cancelarReserva(abonadoIdOverride, currentSessionId);
            } else {
                await cambioButacaService.borrarSesion(currentSessionId);
            }
        } catch (err) {
            if (process.env.NODE_ENV !== 'production') {
                console.error('[useCambioButacaConfirmacion] Error al liberar sesión:', err);
            }
        }
    };

    /**
     * Libera la sesión activa disparando un fetch con `keepalive: true`.
     *
     * Diseñado para ejecutarse dentro de un handler `beforeunload` (cierre
     * de pestaña / recarga). Usa el MISMO endpoint, headers y método que
     * `borrarSesion` desde la capa de servicio — pero se hace `fire-and-forget`
     * (no se await-ea) porque el navegador cierra la página antes de que la
     * promesa pueda resolverse; `keepalive` garantiza que el request salga
     * aunque la pestaña se cierre.
     *
     * Idempotente: si no hay sessionId, retorna sin hacer nada.
     *
     * @param {string|null} sid — sessionId a liberar (si no se pasa, usa el
     *                            sessionId actual del composable).
     */
    const liberarSesionKeepalive = sid => {
        const targetSessionId = sid ?? sessionId.value;
        if (!targetSessionId) return;
        // Pago APM hosted (Bizum) en curso: la navegación a la pasarela
        // dispara beforeunload, pero la reserva debe sobrevivir hasta que
        // el webhook procese el pago completado.
        if (getPagoEnCursoSessionId() === targetSessionId) {
            return;
        }
        const token = localStorage.getItem('token');
        const url = `${config.API_BASE}/f/sesion/${targetSessionId}?api_key=${token}`;
        fetch(url, {
            method: 'DELETE',
            keepalive: true,
            headers: {
                'Authorization': token,
                'Autrollization': token,
                'Hash-Teatro': localStorage.getItem('hash_teatro') || '',
            },
        });
    };

    /**
     * Resetea el estado de sesión sin llamar al backend.
     * Útil cuando la sesión ya fue consumida (tras confirmar exitosamente).
     */
    const limpiarSessionId = () => {
        sessionId.value = null;
    };

    // ─── Timer ────────────────────────────────────────────────────────────

    /**
     * Callback que debe llamarse cuando el CountdownBar llega a 0.
     * Libera la sesión y resetea todo el estado.
     */
    const onTimerExpirado = async() => {
        await liberarSesion();
        reset();
    };

    // ─── Reset ────────────────────────────────────────────────────────────
    const reset = () => {
        sessionId.value = null;
        sessionEndsAt.value = null;
        precioTotal.value = 0;
        importe.value = 0;
        importeCambio.value = 0;
        priceManagement.value = 0;
        jetId.value = null;
        formulario.value = null;
        priceManagementInterno.value = null;
        errorReservar.value = null;
        errorConfirmar.value = null;
        resultadoCambio.value = null;
        resultadoInfo.value = null;
        loadingInfo.value = false;
    };

    // ─── API ──────────────────────────────────────────────────────────────
    return {
        // Reserva
        loadingReservar,
        errorReservar,
        sessionId,
        sessionEndsAt,
        // Pago (datos para el paso de pago)
        precioTotal,
        importe,
        importeCambio,
        priceManagement,
        jetId,
        formulario,
        priceManagementInterno,
        // Confirmación gratuita
        loadingConfirmar,
        errorConfirmar,
        resultadoCambio,
        // Info post-pago
        resultadoInfo,
        loadingInfo,
        // Computed
        requierePago,
        puedeConfirmar,
        mostrarGastosGestion,
        // Métodos
        reservar,
        confirmar,
        consultarInfo,
        liberarSesion,
        liberarSesionKeepalive,
        limpiarSessionId,
        onTimerExpirado,
        reset,
    };
}
